/**
 * ComfortSet Bundle Selector
 * Handles linked model selection, saddle style, seatpost diameter, and cart integration.
 */

class ComfortsetBundleSelector {
  constructor(container) {
    this.container = container;
    this.config = this.#loadConfig();
    this.isAddingToCart = false;

    if (!this.config) return;

    this.usesNativeProductForm =
      this.config.sourceMode === "single_product_option";

    this.modelOptionsContainer = this.container.querySelector(
      "[data-comfortset-model-options]",
    );
    this.modelSection = this.container.querySelector(
      "[data-comfortset-model-section]",
    );
    this.styleOptionsContainer = this.container.querySelector(
      "[data-comfortset-style-options]",
    );
    this.diameterOptionsContainer = this.container.querySelector(
      "[data-comfortset-diameter-options]",
    );
    this.summaryText = this.container.querySelector(
      "[data-comfortset-summary]",
    );
    this.variantInput = this.container.querySelector(
      "[data-comfortset-variant]",
    );
    this.models = this.#buildModels();
    this.selectedModelKey = this.#getInitialModelKey();

    if (!this.models[this.selectedModelKey]) return;

    const initialSelection = this.#getInitialSelection();
    this.selectedStyle = initialSelection.style;
    this.selectedDiameter = initialSelection.diameter;

    this.isInitialRender = true;
    this.#init();
    this.isInitialRender = false;
  }

  #init() {
    if (this.usesNativeProductForm) {
      this.#prepareNativeProductForm();
    } else {
      this.#disableThemeVariantPicker();
    }
    this.#attachEventListeners();
    this.#render();
    if (!this.usesNativeProductForm) {
      this.#overrideAddToCart();
    }
  }

  #loadConfig() {
    const configElement = this.container.querySelector(
      "[data-comfortset-config]",
    );
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error("ComfortSet Selector: Failed to parse config", error);
      return null;
    }
  }

  #buildModels() {
    const normalized = {};

    Object.entries(this.config.models || {}).forEach(([key, model]) => {
      if (
        !model ||
        !Array.isArray(model.variants) ||
        model.variants.length === 0
      )
        return;

      const modelOptionIndex = Number.isInteger(model.modelOptionIndex)
        ? model.modelOptionIndex
        : 0;
      const modelOptionValue = model.modelOptionValue || "";
      const variants =
        this.usesNativeProductForm && modelOptionValue
          ? model.variants.filter(
              (variant) =>
                this.#getOptionValue(variant, modelOptionIndex) ===
                modelOptionValue,
            )
          : model.variants;

      if (variants.length === 0) return;

      const prepared = {
        key,
        productId: model.productId || null,
        handle: model.handle || "",
        url: model.url || "",
        title: model.title || "",
        subtitle: model.subtitle || "",
        image: model.image || this.#getVariantImageUrl(variants[0]),
        styleOptionName: model.styleOptionName || "Stil",
        diameterOptionName: model.diameterOptionName || "Größe",
        modelOptionIndex,
        modelOptionValue,
        styleOptionIndex: Number.isInteger(model.styleOptionIndex)
          ? model.styleOptionIndex
          : 0,
        diameterOptionIndex: Number.isInteger(model.diameterOptionIndex)
          ? model.diameterOptionIndex
          : 1,
        selectedVariantId: model.selectedVariantId
          ? String(model.selectedVariantId)
          : null,
        overrideImages: Array.isArray(model.overrideImages)
          ? model.overrideImages
          : [],
        variantDescriptions: Array.isArray(model.variantDescriptions)
          ? model.variantDescriptions
          : [],
        useEbikeStyleLabels:
          key === "primary" || Boolean(model.useEbikeStyleLabels),
        variants,
      };

      prepared.styleValues = this.#buildStyleValues(prepared);
      prepared.diameterValues = this.#buildDiameterValues(prepared);
      prepared.defaultVariant =
        this.#findFirstAvailableVariant(prepared) ||
        prepared.variants[0] ||
        null;

      normalized[key] = prepared;
    });

    return normalized;
  }

  #buildStyleValues(model) {
    const seen = new Set();
    const values = [];

    model.variants.forEach((variant) => {
      const rawValue = this.#getOptionValue(variant, model.styleOptionIndex);
      if (!rawValue || seen.has(rawValue)) return;

      seen.add(rawValue);
      values.push({
        rawValue,
        displayTitle: this.#formatStyleDisplay(rawValue, {
          useEbikeLabels: model.useEbikeStyleLabels,
        }),
        description: (model.variantDescriptions[values.length] || "").trim(),
        imageUrl:
          model.overrideImages[values.length] ||
          this.#getVariantImageUrl(variant),
        variant,
      });
    });

    return values;
  }

  #buildDiameterValues(model) {
    const seen = new Set();
    const values = [];

    model.variants.forEach((variant) => {
      const rawValue = this.#getOptionValue(variant, model.diameterOptionIndex);
      if (!rawValue || seen.has(rawValue)) return;

      seen.add(rawValue);
      values.push({
        rawValue,
        displayTitle: this.#formatDiameterDisplay(rawValue),
      });
    });

    return values;
  }

  #getInitialModelKey() {
    const initialVariantId = this.#getInitialVariantId();

    if (initialVariantId) {
      const matchingModelKey = Object.keys(this.models).find((key) =>
        this.models[key]?.variants.some(
          (variant) => String(variant.id) === String(initialVariantId),
        ),
      );
      if (matchingModelKey) return matchingModelKey;
    }

    const currentProductModelKey = Object.keys(this.models).find(
      (key) =>
        Number(this.models[key]?.productId) ===
        Number(this.config.currentProductId),
    );

    return currentProductModelKey || "primary";
  }

  #getInitialSelection() {
    const model = this.#getActiveModel();
    const initialVariantId = this.#getInitialVariantId();
    const variant =
      this.#findVariantById(model, initialVariantId) ||
      this.#findVariantById(model, model.selectedVariantId) ||
      model.defaultVariant;

    const style =
      this.#getOptionValue(variant, model.styleOptionIndex) ||
      model.styleValues[0]?.rawValue ||
      "";
    const diameter =
      this.#getOptionValue(variant, model.diameterOptionIndex) ||
      this.#getFirstAvailableDiameterForStyle(model, style) ||
      model.diameterValues[0]?.rawValue ||
      "";

    return { style, diameter };
  }

  #getInitialVariantId() {
    const params = new URLSearchParams(window.location.search);
    const urlVariant = params.get("variant");
    if (urlVariant) return String(urlVariant);

    const form = this.#getProductForm();
    const formVariantInput = form?.querySelector('input[name="id"]');

    if (formVariantInput?.value) return String(formVariantInput.value);
    if (this.config.currentVariantId)
      return String(this.config.currentVariantId);

    return null;
  }

  #getProductForm() {
    const configuredForm = this.config.productFormId
      ? document.getElementById(this.config.productFormId)
      : null;
    const productForm = this.container.closest(".product-form");

    return (
      this.container.closest("form") ||
      configuredForm ||
      productForm?.querySelector('form[is="product-form"]') ||
      productForm?.querySelector('form[action*="/cart/add"]') ||
      null
    );
  }

  #prepareNativeProductForm() {
    const productVariants = this.container
      .closest(".product-form")
      ?.querySelector("product-variants");
    if (productVariants) {
      productVariants.style.display = "none";
    }

    const hiddenVariantInput = this.#getProductForm()?.querySelector(
      'input[name="id"]',
    );
    if (hiddenVariantInput) {
      hiddenVariantInput.disabled = false;
    }
  }

  #disableThemeVariantPicker() {
    const productVariants = this.container
      .closest(".product-form")
      ?.querySelector("product-variants");
    if (productVariants) {
      productVariants.style.display = "none";
      productVariants
        .querySelectorAll("input, select, button")
        .forEach((input) => {
          input.disabled = true;
        });
    }

    const hiddenVariantInput = this.#getProductForm()?.querySelector(
      'input[name="id"]',
    );
    if (hiddenVariantInput) {
      hiddenVariantInput.disabled = true;
    }
  }

  #attachEventListeners() {
    this.container.addEventListener("change", (event) => {
      const target = event.target;

      if (target.matches("[data-comfortset-model-radio]")) {
        this.#handleModelChange(target.value);
        return;
      }

      if (target.matches("[data-comfortset-style-radio]")) {
        this.selectedStyle = target.value;
        this.#ensureDiameterAvailable();
        this.#render();
        return;
      }

      if (target.matches("[data-comfortset-diameter-radio]")) {
        this.selectedDiameter = target.value;
        this.#render();
      }
    });

    this.container.addEventListener(
      "click",
      (event) => {
        if (
          event.target.closest(".popover-text") ||
          event.target.closest('button[is="toggle-button"]')
        ) {
          return;
        }

        event.stopPropagation();
      },
      true,
    );

    this.container.addEventListener("keydown", (event) => {
      if (
        (event.key === "Enter" || event.key === " ") &&
        event.target?.matches?.('[tabindex="0"]')
      ) {
        event.preventDefault();
        event.target.click();
      }
    });
  }

  #handleModelChange(modelKey) {
    const targetModel = this.models[modelKey];
    if (!targetModel || this.selectedModelKey === modelKey) return;

    if (
      Number(targetModel.productId) !== Number(this.config.currentProductId)
    ) {
      this.#navigateToModelProduct(targetModel);
      return;
    }

    this.selectedModelKey = modelKey;
    this.#ensureStyleAvailable();
    this.#ensureDiameterAvailable();
    this.#render();
  }

  #navigateToModelProduct(targetModel) {
    const targetUrl = this.#getModelUrl(targetModel);
    if (!targetUrl) return;

    const matchingVariant =
      this.#findMatchingVariant(
        targetModel,
        this.selectedStyle,
        this.selectedDiameter,
      ) ||
      this.#findFirstAvailableVariantForStyle(
        targetModel,
        this.selectedStyle,
      ) ||
      targetModel.defaultVariant;
    const url = new URL(targetUrl, window.location.origin);

    if (matchingVariant?.id) {
      url.searchParams.set("variant", matchingVariant.id);
    }

    window.location.href = url.toString();
  }

  #render() {
    const model = this.#getActiveModel();
    if (!model) return;

    this.#ensureStyleAvailable();
    this.#ensureDiameterAvailable();
    this.#renderModelOptions();
    this.#renderStyleOptions(model);
    this.#renderDiameterOptions(model);
    this.#updateVariantState();
  }

  #renderModelOptions() {
    if (!this.modelOptionsContainer) return;

    const models = Object.values(this.models);

    if (this.modelSection) {
      this.modelSection.style.display = models.length > 1 ? "" : "none";
    }

    this.modelOptionsContainer.innerHTML = models
      .map((model) => {
        const checked = model.key === this.selectedModelKey ? "checked" : "";
        const imageMarkup = model.image
          ? `<img src="${this.#escapeHtml(model.image)}" alt="${this.#escapeHtml(model.title)}" width="200" height="200" loading="lazy">`
          : "";

        return `
        <div class="comfortset-selector__model-option">
          <input
            type="radio"
            id="comfortset-model-${this.#escapeHtml(model.key)}"
            name="comfortset-model"
            value="${this.#escapeHtml(model.key)}"
            class="comfortset-selector__radio"
            data-comfortset-model-radio
            ${checked}
          >
          <label for="comfortset-model-${this.#escapeHtml(model.key)}" class="comfortset-selector__model-card" tabindex="0">
            <div class="comfortset-selector__model-image">
              <span class="comfortset-selector__checkmark">
                <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg>
              </span>
              ${imageMarkup}
            </div>
            <div class="comfortset-selector__model-info">
              <div class="comfortset-selector__name">${this.#escapeHtml(model.title)}</div>
              <div class="comfortset-selector__subtitle">${this.#escapeHtml(model.subtitle)}</div>
            </div>
          </label>
        </div>
      `;
      })
      .join("");
  }

  #renderStyleOptions(model) {
    if (!this.styleOptionsContainer) return;

    this.styleOptionsContainer.innerHTML = model.styleValues
      .map((style) => {
        const checked = style.rawValue === this.selectedStyle ? "checked" : "";
        const imageMarkup = style.imageUrl
          ? `<img src="${this.#escapeHtml(style.imageUrl)}" alt="${this.#escapeHtml(style.displayTitle)}" width="200" height="200" loading="lazy">`
          : "";
        const descriptionMarkup = this.#buildStyleDescriptionMarkup(
          style.description,
        );

        return `
        <div class="comfortset-selector__style-option">
          <input
            type="radio"
            id="comfortset-style-${this.#slugify(style.rawValue)}"
            name="comfortset-style"
            value="${this.#escapeHtml(style.rawValue)}"
            class="comfortset-selector__radio"
            data-comfortset-style-radio
            ${checked}
          >
          <label for="comfortset-style-${this.#slugify(style.rawValue)}" class="comfortset-selector__style-card" tabindex="0">
            <div class="comfortset-selector__style-image">
              <span class="comfortset-selector__checkmark">
                <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg>
              </span>
              ${imageMarkup}
            </div>
            <div class="comfortset-selector__style-info">
              <div class="comfortset-selector__name">${this.#escapeHtml(style.displayTitle)}</div>
              ${descriptionMarkup}
            </div>
          </label>
        </div>
      `;
      })
      .join("");
  }

  #buildStyleDescriptionMarkup(description) {
    const bullets = String(description || "")
      .split(",")
      .map((bullet) => bullet.trim())
      .filter(Boolean)
      .map((bullet) => `<li>&bull; ${this.#escapeHtml(bullet)}</li>`)
      .join("");

    if (!bullets) return "";

    return `
      <div class="comfortset-selector__style-description">
        <span class="comfortset-selector__style-description-label">Empfohlen für:</span>
        <ul class="comfortset-selector__style-description-list">
          ${bullets}
        </ul>
      </div>
    `;
  }

  #renderDiameterOptions(model) {
    if (!this.diameterOptionsContainer) return;

    this.diameterOptionsContainer.innerHTML = model.diameterValues
      .map((diameter) => {
        const variant = this.#findMatchingVariant(
          model,
          this.selectedStyle,
          diameter.rawValue,
        );
        const isAvailable = Boolean(variant?.available);
        const checked =
          diameter.rawValue === this.selectedDiameter ? "checked" : "";
        const disabled = isAvailable ? "" : "disabled";

        return `
        <div class="comfortset-selector__diameter-option">
          <input
            type="radio"
            id="comfortset-diameter-${this.#slugify(diameter.rawValue)}"
            name="comfortset-diameter"
            value="${this.#escapeHtml(diameter.rawValue)}"
            class="comfortset-selector__radio"
            data-comfortset-diameter-radio
            ${checked}
            ${disabled}
          >
          <label for="comfortset-diameter-${this.#slugify(diameter.rawValue)}" class="comfortset-selector__diameter-card" tabindex="0">
            <span class="comfortset-selector__diameter-radio-dot"></span>
            <span>${this.#escapeHtml(diameter.displayTitle)}</span>
          </label>
        </div>
      `;
      })
      .join("");
  }

  #updateVariantState() {
    const model = this.#getActiveModel();
    const variant = this.#getSelectedVariant();
    if (!model || !variant) return;

    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (this.summaryText) {
      this.summaryText.textContent = `${model.title}: ${this.#formatStyleDisplay(this.selectedStyle, { useEbikeLabels: model.useEbikeStyleLabels })} / ${this.#formatDiameterDisplay(this.selectedDiameter)}`;
    }

    this.#updateProductFormVariant(variant);
    if (!this.usesNativeProductForm && !this.isInitialRender) {
      this.#updatePrice(variant);
    }
    this.#updateAddToCartButton(variant);
    if (this.usesNativeProductForm) {
      this.#updateUrl(variant);
    }
    this.#scrollToVariantImage(variant);
  }

  #updateProductFormVariant(variant) {
    if (
      Number(this.#getActiveModel().productId) !==
      Number(this.config.currentProductId)
    )
      return;

    const form = this.#getProductForm();
    if (!form) return;

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.disabled = false;
      idInput.value = variant.id;
    }

    if (this.isInitialRender) return;

    const variantForDispatch = { ...variant };
    if (
      !variantForDispatch.featured_media &&
      (variant.featured_image || variant.image)
    ) {
      variantForDispatch.featured_media =
        variant.featured_image || variant.image;
    }

    form.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: { variant: variantForDispatch },
      }),
    );
  }

  #updateUrl(variant) {
    if (!variant?.id || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url.toString());
  }

  #updatePrice(variant) {
    document.querySelectorAll("[data-product-price-list]").forEach((priceList) => {
      const priceElement = priceList.querySelector("[data-ed-price]");
      const comparePriceElement = priceList.querySelector("[data-ed-compare-price]");
      const savingsBadge = priceList.querySelector(".price-savings-badge");

      if (priceElement && typeof variant.price === "number") {
        priceElement.textContent = this.#formatMoney(variant.price);
        priceElement.setAttribute("data-ed-price", variant.price);
      }

      if (comparePriceElement) {
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          comparePriceElement.textContent = this.#formatMoney(
            variant.compare_at_price,
          );
          comparePriceElement.setAttribute(
            "data-ed-compare-price",
            variant.compare_at_price,
          );
          comparePriceElement.style.display = "";
        } else {
          comparePriceElement.style.display = "none";
        }
      }

      if (savingsBadge) {
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          const savings = variant.compare_at_price - variant.price;
          const percentage = Math.round(
            (savings * 100) / variant.compare_at_price,
          );
          const savingsLabel = priceList.closest(".nc-1-ab-test-a")
            ? `Du sparst ${this.#formatMoney(savings)} (${percentage}%)`
            : window.themeVariables.strings.productSaveBadge.replace(
                "@value@",
                percentage,
              );
          savingsBadge.textContent = savingsLabel;
          savingsBadge.style.display = "";
        } else {
          savingsBadge.style.display = "none";
        }
      }
    });
  }

  #updateAddToCartButton(variant) {
    const addToCartButton = document.querySelector(".product-form__add-button");
    if (!addToCartButton) return;

    const isAvailable = Boolean(variant.available);
    addToCartButton.disabled = !isAvailable;

    if (isAvailable && addToCartButton.dataset.buttonContent) {
      addToCartButton.textContent = addToCartButton.dataset.buttonContent;
    }
  }

  #overrideAddToCart() {
    let productForm = this.#getProductForm();
    if (!productForm) {
      productForm = document.querySelector('form[action*="/cart/add"]');
    }
    if (!productForm) {
      console.error("ComfortSet Selector: Product form not found");
      return;
    }

    productForm.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.#handleAddToCart();
        return false;
      },
      true,
    );
  }

  async #handleAddToCart() {
    if (this.isAddingToCart) return;

    this.isAddingToCart = true;
    const addToCartButton = document.querySelector(".product-form__add-button");

    if (addToCartButton) {
      addToCartButton.disabled = true;
      addToCartButton.setAttribute("aria-busy", "true");
    }

    try {
      const variant = this.#getSelectedVariant();
      if (!variant) throw new Error("Bitte wähle eine Variante aus.");
      if (!variant.available)
        throw new Error("Diese Variante ist aktuell nicht verfügbar.");

      const activeModel = this.#getActiveModel();
      const item = {
        id: parseInt(variant.id, 10),
        quantity: 1,
        properties: {
          _bundle: "comfortset-selector",
          _comfortset_model: activeModel.title,
          _comfortset_style: this.#formatStyleDisplay(this.selectedStyle, {
            useEbikeLabels: activeModel.useEbikeStyleLabels,
          }),
          _comfortset_diameter: this.#formatDiameterDisplay(
            this.selectedDiameter,
          ),
        },
      };

      const cartData = await this.#addToCartAPI(item);
      this.#refreshCart(cartData);
    } catch (error) {
      console.error("ComfortSet Selector: Error adding to cart", error);
      this.#showErrorMessage(error.message);
    } finally {
      if (addToCartButton) {
        addToCartButton.disabled = !this.#getSelectedVariant()?.available;
        addToCartButton.setAttribute("aria-busy", "false");
      }
      this.isAddingToCart = false;
    }
  }

  async #addToCartAPI(item) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [item],
        sections: "mini-cart",
      }),
      credentials: "include",
    });

    const text = await response.text();

    if (!response.ok) {
      let errorMessage = "Failed to add to cart";
      try {
        const error = JSON.parse(text);
        errorMessage = error.message || error.description || errorMessage;
      } catch (error) {
        errorMessage = text || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return JSON.parse(text);
  }

  async #refreshCart(cartDataFromAdd, options = {}) {
    const { openDrawer = true, showNotification = true } =
      typeof options === "boolean"
        ? { openDrawer: options, showNotification: options }
        : options;

    try {
      let cartData;

      if (
        cartDataFromAdd &&
        typeof cartDataFromAdd === "object" &&
        cartDataFromAdd.sections
      ) {
        if (typeof cartDataFromAdd.item_count === "number") {
          cartData = cartDataFromAdd;
        } else {
          const cartResponse = await fetch("/cart.js");
          cartData = await cartResponse.json();
          if (cartData && typeof cartData === "object") {
            cartData.sections = cartDataFromAdd.sections;
          }
        }
      } else {
        const [cartResponse, sectionsResponse] = await Promise.all([
          fetch("/cart.js"),
          fetch("/?sections=mini-cart"),
        ]);
        cartData = await cartResponse.json();
        const sectionsData = await sectionsResponse.json();
        if (cartData && typeof cartData === "object") {
          cartData.sections = sectionsData;
        }
      }

      if (cartData && typeof cartData === "object") {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:updated", {
            bubbles: true,
            detail: { cart: cartData },
          }),
        );
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartData,
              openMiniCart:
                openDrawer &&
                window.themeVariables?.settings?.cartType === "drawer",
            },
          }),
        );
      }

      if (showNotification) {
        this.#showSuccessNotification();
        setTimeout(
          () =>
            this.#refreshCart(null, {
              openDrawer: false,
              showNotification: false,
            }),
          1500,
        );
      }
    } catch (error) {
      console.error("ComfortSet Selector: Error refreshing cart", error);
      const cartDrawer = document.querySelector(
        "cart-drawer, [data-cart-drawer]",
      );
      if (
        cartDrawer &&
        window.themeVariables?.settings?.cartType === "drawer"
      ) {
        cartDrawer.open = true;
      }
    }
  }

  #getActiveModel() {
    return this.models[this.selectedModelKey] || this.models.primary;
  }

  #getSelectedVariant() {
    return this.#findMatchingVariant(
      this.#getActiveModel(),
      this.selectedStyle,
      this.selectedDiameter,
    );
  }

  #ensureStyleAvailable() {
    const model = this.#getActiveModel();
    if (!model?.styleValues.length) return;

    const hasStyle = model.styleValues.some(
      (style) => style.rawValue === this.selectedStyle,
    );
    if (!hasStyle) {
      this.selectedStyle = model.styleValues[0].rawValue;
    }
  }

  #ensureDiameterAvailable() {
    const model = this.#getActiveModel();
    if (!model?.diameterValues.length) return;

    const selectedVariant = this.#findMatchingVariant(
      model,
      this.selectedStyle,
      this.selectedDiameter,
    );
    if (selectedVariant?.available) return;

    this.selectedDiameter =
      this.#getFirstAvailableDiameterForStyle(model, this.selectedStyle) ||
      model.diameterValues[0].rawValue;
  }

  #getFirstAvailableDiameterForStyle(model, style) {
    const variant = this.#findFirstAvailableVariantForStyle(model, style);
    return this.#getOptionValue(variant, model.diameterOptionIndex);
  }

  #findFirstAvailableVariantForStyle(model, style) {
    return (
      model?.variants.find(
        (variant) =>
          variant.available &&
          this.#getOptionValue(variant, model.styleOptionIndex) === style,
      ) || null
    );
  }

  #findFirstAvailableVariant(model) {
    return model?.variants.find((variant) => variant.available) || null;
  }

  #findMatchingVariant(model, style, diameter) {
    if (!model) return null;

    return (
      model.variants.find(
        (variant) =>
          this.#getOptionValue(variant, model.styleOptionIndex) === style &&
          this.#getOptionValue(variant, model.diameterOptionIndex) === diameter,
      ) || null
    );
  }

  #findVariantById(model, variantId) {
    if (!model || !variantId) return null;
    return (
      model.variants.find(
        (variant) => String(variant.id) === String(variantId),
      ) || null
    );
  }

  #getOptionValue(variant, optionIndex) {
    if (!variant || !Array.isArray(variant.options)) return "";
    return String(variant.options[optionIndex] || "").trim();
  }

  #getModelUrl(model) {
    if (model.url) return model.url;
    if (model.handle) {
      const root = window.Shopify?.routes?.root || "/";
      return `${root.replace(/\/$/, "")}/products/${model.handle}`;
    }

    return "";
  }

  #scrollToVariantImage(variant) {
    if (!variant) return;

    let mediaId = null;
    if (variant.featured_media?.id) {
      mediaId = variant.featured_media.id;
    } else if (variant.image?.id) {
      mediaId = variant.image.id;
    } else if (variant.featured_image?.id) {
      mediaId = variant.featured_image.id;
    }

    if (!mediaId) return;

    const swiperEl = document.querySelector(
      '[id*="-media-list"][data-transition-effect]',
    );
    if (!swiperEl?.swiper) {
      setTimeout(() => this.#scrollToVariantImage(variant), 100);
      return;
    }

    const targetSlide = swiperEl.querySelector(
      `.swiper-slide[data-media-id="${mediaId}"]:not(.is-filtered)`,
    );
    if (!targetSlide) return;

    const slides = Array.from(
      swiperEl.querySelectorAll(".swiper-slide:not(.is-filtered)"),
    );
    const slideIndex = slides.indexOf(targetSlide);
    if (slideIndex >= 0) {
      swiperEl.swiper.slideTo(slideIndex);
    }
  }

  #getVariantImageUrl(variant) {
    const potentialImage =
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_media?.src ||
      variant?.featured_image?.src ||
      variant?.image?.src ||
      variant?.image;

    return this.#normalizeUrl(potentialImage || "");
  }

  #normalizeUrl(url) {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("//")) return `https:${url}`;
    return url;
  }

  #formatStyleDisplay(value, { useEbikeLabels = false } = {}) {
    const trimmed = String(value || "")
      .split(" - ")[0]
      .trim();

    if (useEbikeLabels) {
      const ebikeLabels = {
        Trekking: "Trekking-E-Bike",
        City: "City-E-Bike",
        MTB: "E-MTB",
        Mountainbike: "E-MTB",
      };
      if (ebikeLabels[trimmed]) return ebikeLabels[trimmed];
    }

    if (trimmed === "Mountainbike") return "MTB";
    return trimmed;
  }

  #formatDiameterDisplay(value) {
    return String(value || "").trim();
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return `${amount.toFixed(2).replace(".", ",")} €`;
  }

  #showSuccessNotification() {
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #2e9e7b;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      font-size: 14px;
      font-weight: 500;
    `;
    notification.textContent = "Zum Warenkorb hinzugefügt";
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.transition = "opacity 0.3s";
      notification.style.opacity = "0";
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  #showErrorMessage(message) {
    document.documentElement.dispatchEvent(
      new CustomEvent("product:add-error", {
        bubbles: true,
        detail: { message },
      }),
    );
    alert(message);
  }

  #slugify(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  #escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}

const initComfortsetBundleSelectors = () => {
  document
    .querySelectorAll("[data-comfortset-selector]")
    .forEach((container) => {
      if (container.dataset.comfortsetInitialized === "true") return;
      container.dataset.comfortsetInitialized = "true";
      new ComfortsetBundleSelector(container);
    });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initComfortsetBundleSelectors);
} else {
  initComfortsetBundleSelectors();
}
